/**
 * Spiegeltiefe unter oder über Flur (Nutzer-Auftrag 02.10.2026).
 *
 * Die Regel:
 *
 * - Gespeichert wird EINE Zahl in cm, ab Oberkante Mineralboden wie jede Tiefe
 *   (`extendedHeader.spiegelTiefe`). Über Flur ist sie negativ: 10 cm über Flur
 *   = -10. Ein eigenes Ja/Nein-Feld wäre gefährlich gewesen: Jede Stelle, die
 *   davon nichts weiß (Filter, GIS-Export, Excel), läse 10 cm über Flur als
 *   10 cm unter Flur, und niemandem fiele es auf.
 * - Flur ist ohne Filz die Oberkante Mineralboden (der erste richtige
 *   Horizont), bei einem Filz die Oberkante Ol (Nutzer-Entscheidung 02.10.2026:
 *   „25 cm über Flur heißt bei Filzen 25 cm über Ol"). Ab Flur wird angezeigt
 *   UND eingegeben, in beide Richtungen.
 * - Bei einem Filz weicht das Feld damit von den übrigen Tiefen ab: Die werden
 *   ab Mineralboden eingegeben und ab Ol angezeigt (filz-tiefen.ts). Das Feld
 *   sagt es deshalb in seiner Beschriftung (`FILZ_AB_OL_TEXT`).
 * - Angezeigt wird mit derselben Rechnung wie jede Tiefe (`angezeigteTiefe` in
 *   filz-tiefen.ts): gespeichert plus Auflage. Liegt das Ergebnis unter 0,
 *   steht der Spiegel über Flur.
 * - Eingegeben wird immer eine positive Zahl; ein Knopf am Feld sagt, in welche
 *   Richtung (`FlurTiefeInputComponent`). Wer trotzdem ein Minus tippt, meint
 *   „über Flur".
 */

export type FlurRichtung = 'unter' | 'ueber';

export const UNTER_FLUR_TEXT = 'unter Flur';
export const UEBER_FLUR_TEXT = 'über Flur';

/** An der Beschriftung des Feldes, wenn ein Filz mit Auflage vorliegt. */
export const FILZ_AB_OL_TEXT = 'Filz: ab Oberkante Ol';

/** Komma statt Punkt, wie `de` im PDF - hier ohne dessen Abhängigkeiten, das Grubenbild liest mit. */
function de(n: number): string {
	return String(n).replace('.', ',');
}

/** Auf zwei Nachkommastellen, wie `angezeigteTiefe` - sonst bliebe Rechenrauschen stehen. */
function runde(n: number): number {
	return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Tiefe ab Flur aus dem gespeicherten Wert (ab Oberkante Mineralboden): bei
 * einem Filz plus Auflage (`versatz`, filz-tiefen.ts). Leer bleibt leer.
 */
export function abFlur(wert: number | null | undefined, versatz: number): number | null | undefined {
	if (typeof wert !== 'number' || !Number.isFinite(wert)) return wert;
	return runde(wert + versatz);
}

/** Der Rückweg: gespeichert wird ab Oberkante Mineralboden. */
export function abMineralboden(tiefeAbFlur: number | null | undefined, versatz: number): number | null | undefined {
	if (typeof tiefeAbFlur !== 'number' || !Number.isFinite(tiefeAbFlur)) return tiefeAbFlur;
	return runde(tiefeAbFlur - versatz);
}

/** Die Richtung, die ein gespeicherter Wert hat. Ohne Richtung (leer, 0) bleibt die bisherige. */
export function richtungAus(wert: number | null | undefined, bisher: FlurRichtung): FlurRichtung {
	if (typeof wert !== 'number' || !Number.isFinite(wert) || wert === 0) return bisher;
	return wert < 0 ? 'ueber' : 'unter';
}

/** Was im Feld steht: der Betrag. Leer bleibt leer. */
export function betragAus(wert: number | null | undefined): number | null | undefined {
	if (typeof wert !== 'number' || !Number.isFinite(wert)) return wert;
	return Math.abs(wert);
}

/**
 * Der zu speichernde Wert aus Feld und Knopf. Ein getipptes Minus heisst
 * „über Flur", egal wie der Knopf steht. Leer bleibt leer, 0 bleibt 0 (keine -0).
 */
export function wertAus(betrag: number | null | undefined, richtung: FlurRichtung): number | null | undefined {
	if (typeof betrag !== 'number' || !Number.isFinite(betrag)) return betrag;
	if (betrag === 0) return 0;
	if (betrag < 0) return betrag;
	return richtung === 'ueber' ? -betrag : betrag;
}

/**
 * Für Tabellen, deren Kopf schon „Spiegeltiefe" sagt: „80" unter Flur (wie
 * bisher, ohne Einheit), „10 cm über Flur" darüber. `angezeigt` ist die Tiefe
 * ab Flur, also schon mit der Auflage eines Filzes (`angezeigteTiefe`).
 */
export function flurTiefeText(angezeigt: number | null | undefined): string {
	if (typeof angezeigt !== 'number' || !Number.isFinite(angezeigt)) return '';
	return angezeigt < 0 ? `${de(-angezeigt)} cm ${UEBER_FLUR_TEXT}` : de(angezeigt);
}

/** Für eine Zeile mit Einheit (Grubenbild): „80 cm" oder „10 cm über Flur". */
export function wasserstandText(angezeigt: number): string {
	return angezeigt < 0 ? `${de(-angezeigt)} cm ${UEBER_FLUR_TEXT}` : `${de(angezeigt)} cm`;
}
